/**
 * The one place the booking app computes money.
 *
 * There used to be three implementations in this app alone — the reducer's
 * `get_total_price`, `hooks/usePrice.ts`, and a byte-identical copy of the
 * latter inside `components/utils/index.tsx` — plus a fourth on the landing
 * page and a fifth in the dashboard. They did not agree:
 *
 *   - the reducer rounded `Math.round(subtotal - discount)` while usePrice
 *     rounded `subtotal - Math.round(discount)`, so the subtotal shown in the
 *     sidebar could differ by a dollar from the total actually posted;
 *   - `Cart.tsx` computed its Tax line at **8.88%** while every other path used
 *     8.875%, so the printed line items did not add up to the printed total;
 *   - the cleaning-type price table here was a hardcoded, unversioned copy.
 *
 * Everything now routes through `@homero/pricing`, which is also what the
 * server uses, so the number the customer sees is computed by the same code
 * that computes the number they are charged.
 */

import {
  ADD_ON_COST_CENTS,
  CLEANING_TYPE_LABELS,
  centsToDollars,
  dollarsToCents,
  formatCents,
  getAddOnsCostCents,
  getBookingPrice,
  includesFreeExtras,
  resolveCleaningType,
  withProximityTier,
  withSameDayForDate,
  withoutDayOfWeekVariation,
  type CleaningTypeId,
  type PriceBreakdown,
  type ProximityTier,
  receiptAmounts,
} from "@homero/pricing";
import { currentPricingConfig } from "./pricing-config";
import { quoteKey } from "./proximity";

export { formatCents, ADD_ON_COST_CENTS, currentPricingConfig };

/** The slice of redux state that determines the price. */
export interface BookingPriceState {
  bedrooms: string;
  bathrooms: string;
  /** The legacy wire format: the cleaning type's price, as a string. */
  types: string;
  /** The frequency discount, as a whole percentage. */
  occurance: number;
  isFlexible: boolean;
  /** Promo code discount, in dollars, as the store holds it. */
  codeDiscount: number;
  addOns: { type: string[]; cost: number };
  /**
   * The date the cleaning is scheduled for, once the customer has picked one.
   * Decides which day's surge applies — distinct from "now", which decides which
   * rate card applies.
   *
   * Named to match the store's own `cleanDate`, because callers pass the whole
   * redux slice into these functions and rely on structural typing. A different
   * name here would type-check and silently arrive undefined, which would show
   * every customer an unsurged price and then charge them a surged one.
   */
  cleanDate?: string;
  /** Whether the customer took Same-Day. */
  sameDay?: boolean;
  /**
   * The Same-Day surcharge for this date, as the server reported it.
   *
   * Held rather than looked up because the published config carries no surcharge
   * grid — see `docs/adr/0006-pricing-config-is-public-integrity-is-not.md`. It is
   * folded into a per-date config below so the engine still prices through its
   * ordinary path, rather than this app growing a second way to add money.
   */
  sameDaySurchargePercent?: number;
  /**
   * The area-pricing tier the server quoted for this ZIP, date and arrival, and
   * what it is worth. Held for the same reason as the Same-Day surcharge: the
   * published config carries no density, so the client cannot work it out.
   */
  proximityTier?: ProximityTier;
  proximityPercent?: number;
  /**
   * The question that tier answered (`quoteKey`). When the store carries one
   * and it is not this state's question, the tier is for some other date, time
   * or ZIP and prices as none until the new answer arrives.
   */
  proximityFor?: string;
  zip?: string;
  cleanTime?: string;
  arrivalWindow?: string;
  arrivalWindowLabel?: string;
}

/**
 * The area-pricing tier this cart is priced with: the quoted one, on any booking
 * with a date.
 *
 * Plans included, as of the 15 Sep 2026 feedback. A tier belongs to one date's
 * neighbourhood, which is why a plan used to get none — but the answer to that
 * is to charge it once, not to withhold it. The server settles it on the first
 * invoice (`splitRecurringPrice`, and a Stripe coupon with `duration: "once"`),
 * so what this returns is the tier for the *first* cleaning, which is the one
 * this cart is quoting. The ongoing price is `ongoingPrice` below.
 *
 * The server applies the same rule (`tierForBooking`), and the two must agree or
 * checkout refuses the total.
 */
export function areaTierFor(state: BookingPriceState): ProximityTier {
  if (!normalizeServiceDate(state.cleanDate)) return "none";
  if (state.proximityFor !== undefined && state.proximityFor !== quoteKey(state)) return "none";
  return state.proximityTier ?? "none";
}

/**
 * A new booking is priced as of now, which is what makes the rate card
 * selection correct for the customer in front of us. (The server prices an
 * *existing* booking by its stored `created_at`, which is a different thing and
 * why that parameter is required there.)
 */
function now(): Date {
  return new Date();
}

export function resolveTypeId(types: string): CleaningTypeId {
  return resolveCleaningType(types, now()) ?? "standard";
}

/**
 * The store's `cleanDate` as the engine wants it, or absent.
 *
 * Both writers format it with `moment(...).format("YYYY-MM-DD")` — the date picker
 * in `StepTwo` and `getNextAvailableDate` — so the only other value it ever holds
 * is the initial empty string, which means "the customer has not picked a date
 * yet".
 *
 * Anything non-empty is passed straight through rather than being sanity-checked
 * and dropped. A malformed date here would be a bug upstream, and dropping it
 * would show the customer a price with no surge while the server charged one with
 * it — the "Invalid total amount" failure, arrived at from a new direction. Better
 * that the engine refuses the value.
 */
function normalizeServiceDate(cleanDate: string | undefined): string | undefined {
  const trimmed = (cleanDate ?? "").trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * The full breakdown, in cents.
 *
 * `serviceDate` is what lets the day-of-week surge apply. It is optional because
 * the customer picks a date partway through the funnel and the earlier steps still
 * have to show a price; with no date the engine prices without surge rather than
 * guessing a day. Once a date is chosen the displayed total includes that day's
 * surge, folded into the appointment value rather than itemised.
 */
export function computeBookingPrice(state: BookingPriceState): PriceBreakdown {
  const serviceDate = normalizeServiceDate(state.cleanDate);
  const cleaningType = resolveTypeId(state.types);

  /**
   * A Same-Day booking prices against a config carrying the surcharge the server
   * quoted for this one date. Without that the engine refuses outright, which is
   * the correct default — the published config has no surcharge grid, and pricing
   * Same-Day at zero surcharge would show the customer a bargain we then charge
   * them more than.
   */
  const config =
    state.sameDay && serviceDate
      ? withSameDayForDate(
          currentPricingConfig(),
          serviceDate,
          state.sameDaySurchargePercent || 0,
          cleaningType
        )
      : serviceDate
      ? currentPricingConfig()
      : /**
         * No date yet, so collapse the day-dependent axes before pricing.
         *
         * The comment above this function has always promised that a dateless
         * cart "prices without surge rather than guessing a day", but the engine
         * was handed the live config regardless — and `resolveDayOfWeek` throws
         * on a day-varying config with no date rather than guessing. The two
         * contracts only agreed while every day carried the same rates, which is
         * true of the bundled fallback card and false the moment an admin sets a
         * single surge or one day's Flexible Time discount. The funnel rendered
         * on the fallback and then white-screened the instant the real config
         * arrived, so turning the feature on broke every booking.
         *
         * `withoutDayOfWeekVariation` keeps the live rate card and neutralises
         * only what a missing date makes unanswerable, which makes the promise
         * above true again. The total this produces is a lower bound on the surge
         * axis — callers must label it "from", not present it as a quote.
         */
        withoutDayOfWeekVariation(currentPricingConfig());

  /**
   * Folded into the config, as Same-Day is above, so the engine prices it
   * through its ordinary path. The engine refuses a tier the config does not
   * offer, which is why the percentage the server quoted travels with it.
   */
  const tier = areaTierFor(state);
  const priced =
    tier === "none" ? config : withProximityTier(config, tier, state.proximityPercent || 0);

  return getBookingPrice(
    {
      bedrooms: state.bedrooms === "Studio" ? 0 : Number(state.bedrooms) || 0,
      bathrooms: Number(state.bathrooms) || 0,
      cleaningType,
      /**
       * The real arrival, not an empty string.
       *
       * This passed "" for every fixed time, because the only thing the engine
       * read `clean_time` for was the Flexible Time discount, and "" is not
       * "flexible". Time-of-day pricing reads it too, so an empty value here
       * meant a rule the admin had switched on silently never reached the cart
       * while the server charged it -- the exact shape of mismatch the total
       * check sends customers back for.
       */
      clean_time: state.isFlexible ? "flexible" : state.cleanTime ?? "",
      frequencyDiscountPercent: state.occurance || 0,
      add_ons: state.addOns?.type ?? [],
      created_at: now(),
      service_date: serviceDate,
      same_day: Boolean(state.sameDay),
      proximity_tier: tier,
    },
    dollarsToCents(state.codeDiscount || 0),
    priced
  );
}

/**
 * What a plan costs from its second cleaning on: the same booking with the
 * nearby discount taken away.
 *
 * The mirror of `splitRecurringPrice` on the server, and it has to stay one —
 * the customer is shown this number and Stripe bills it as `unit_amount`, so a
 * cart that computed it differently would quote a plan the subscription does not
 * charge.
 *
 * Null when there is nothing to say: a one-time booking has no "ongoing", and a
 * plan with no cluster discount already shows its recurring price as the total.
 * A travel premium recurs (a lonely trip is lonely every cycle), so it is not
 * stripped here.
 */
export function ongoingPrice(state: BookingPriceState): number | null {
  if ((Number(state.occurance) || 0) === 0) return null;
  if (areaTierFor(state) !== "cluster") return null;
  try {
    return computeBookingPrice({
      ...state,
      proximityTier: "none",
      proximityPercent: 0,
      proximityFor: undefined,
    }).totalCents;
  } catch {
    return null;
  }
}

/**
 * The subtotal and total in dollars, for the components that still read dollars
 * from the store.
 *
 * Exact today, because every V1-V4 price is a whole dollar and the legacy
 * engine returns whole dollars. Once V5 is live these carry cents and the store
 * should hold `*Cents` directly — that change lands with the server quote
 * endpoint, since the API and its `total_amount` column have to accept cents
 * on the same deploy.
 */
/** What the cart's bottom line needs on top of a priced breakdown. */
export interface CartSummaryState extends BookingPriceState {
  tip?: number | string;
  user?: { credits?: number | string };
}

export interface CartSummary {
  breakdown: PriceBreakdown;
  tipCents: number;
  creditsCents: number;
  creditsAppliedCents: number;
  /** After credits — what the card is actually asked for. */
  amountDueCents: number;
  /** Every saving on the booking, summed. */
  discountCents: number;
  /** What a plan costs from its second cleaning on, or null. */
  ongoingCents: number | null;
  /** Whether a date has been chosen, which is what makes the total a quote. */
  hasServiceDate: boolean;
  /** `Total:`, `Total from:` or `First cleaning:`. */
  totalLabel: string;
}

/**
 * The cart's bottom line: credits and tip applied, and the name the number
 * goes by.
 *
 * Here rather than inside the cart because it is no longer the only surface
 * showing it — the sticky bar stands in for the whole cart wherever the layout
 * stacks it below the fold, and a bar that recomputed this would be one more
 * independent implementation of the booking total. That is the failure this
 * file already carries three comments about.
 *
 * The label matters as much as the figure. "Total" beside one of two different
 * numbers is the sentence a customer reads as the price of every cleaning and
 * then disputes on the second invoice, and before a date is picked the price is
 * a floor rather than a quote.
 */
export function cartSummary(state: CartSummaryState): CartSummary {
  const breakdown = computeBookingPrice(state);
  const tipCents = dollarsToCents(Number(state.tip) || 0);
  const creditsCents = dollarsToCents(Number(state.user?.credits) || 0);
  const payableCents = breakdown.totalCents + tipCents;
  const creditsAppliedCents = Math.min(creditsCents, payableCents);
  const ongoingCents = ongoingPrice(state);

  /**
   * Trimmed rather than tested for truthiness, so this agrees with
   * `computeBookingPrice`'s own `normalizeServiceDate`. A whitespace-only
   * `cleanDate` is truthy but prices as dateless, which would label a floor
   * price as a firm total — the one combination this exists to prevent.
   */
  const hasServiceDate = Boolean((state.cleanDate ?? "").trim());

  return {
    breakdown,
    tipCents,
    creditsCents,
    creditsAppliedCents,
    amountDueCents: payableCents - creditsAppliedCents,
    /*
      From the receipt helper rather than added up here, so a fifth kind of
      saving reaches every surface at once. Enumerated by hand, the sticky
      bar's "You save" is the one place a new discount would silently go
      missing.
    */
    discountCents: receiptAmounts(breakdown).discountCents,
    ongoingCents,
    hasServiceDate,
    totalLabel:
      ongoingCents !== null
        ? "First cleaning:"
        : hasServiceDate
        ? "Total:"
        : "Total from:",
  };
}

/**
 * The appointment value the cart and the mobile summary print: surge folded in,
 * as `receiptAmounts` folds it, because surge is what the appointment costs that
 * day rather than a fee with a line of its own.
 */
export function appointmentValueCentsOf(breakdown: PriceBreakdown): number {
  return breakdown.baseCents + breakdown.dayOfWeekSurgeCents;
}

/** One named saving, as the cart and the mobile summary both print it. */
export type DiscountLine = { tag: string; label: string; cents: number };

/**
 * The savings on a booking, itemised and named.
 *
 * Here rather than inside the cart because the cart is no longer the only
 * surface that prints them: on a phone the sidebar does not exist at all for
 * steps one to three (`displayBottomNav` is the first term of `hideSidebar` in
 * `App.tsx`), so the summary behind the sticky bar is the *only* place those
 * steps can show what a customer saved. Two independent copies of this list is
 * how the fifth kind of saving reaches one surface and not the other — the
 * failure this file already carries three comments about.
 *
 * Reads every amount off the breakdown, so the parts add up to the number
 * beside them by construction.
 */
export function discountLinesFor(
  breakdown: PriceBreakdown,
  occurance: number | string,
  code?: string | null
): DiscountLine[] {
  const lines: DiscountLine[] = [];
  /*
    Named, and only named. Each label used to carry the terms it was earned on
    too — "Every 2 weeks · 15% off", "Flexible arrival · 9am–4pm" — which is
    more words than the label column holds in the sidebar at most widths. They
    wrapped, and a second line opening with a "·" reads as a rendering fault
    rather than a continuation.

    Cut rather than wrapped, because the terms are not this list's job: the
    rate is on the frequency tile the customer just chose, the window is on the
    arrival card they just picked, and the money either bought them is the
    number at the end of this row. It is the same call the nearby discount was
    given below — a percentage next to an amount is a second, worse way of
    saying what the amount already says.
  */
  if (breakdown.frequencyDiscountCents > 0) {
    lines.push({
      tag: "PLAN",
      label: frequencyLabelFor(occurance),
      cents: breakdown.frequencyDiscountCents,
    });
  }
  if (breakdown.flexibleDiscountCents > 0) {
    lines.push({
      tag: "TIME",
      label: "Flexible arrival",
      cents: breakdown.flexibleDiscountCents,
    });
  }
  if (breakdown.proximityDiscountCents > 0) {
    lines.push({
      tag: "AREA",
      // Named as everywhere else. No "10% off" beside it: the percentage is of
      // a base price the cart never shows, so beside the amount it read wrong.
      // The rule the two lines above now follow as well.
      label: "Nearby discount",
      cents: breakdown.proximityDiscountCents,
    });
  }
  if (breakdown.promoDiscountCents > 0) {
    lines.push({
      tag: "CODE",
      label: code ? String(code).toUpperCase() : "Promo code",
      cents: breakdown.promoDiscountCents,
    });
  }
  return lines;
}

export function computeBookingPriceDollars(state: BookingPriceState): {
  subTotal: number;
  totalPrice: number;
  tax: number;
  appointmentValue: number;
} {
  const b = computeBookingPrice(state);
  return {
    subTotal: centsToDollars(b.subTotalCents),
    totalPrice: centsToDollars(b.totalCents),
    tax: centsToDollars(b.taxCents),
    /**
     * Surge included, matching the cart, `toBookingLines` and the dashboard's
     * `getPricing`. `baseCents` alone is the pre-surge figure, and handing that
     * out under the name "appointment value" is what let the cart's lines stop
     * summing once a day carried a surge. Nothing reads this back off the store
     * today, so this is fixing the trap rather than a live symptom.
     */
    appointmentValue: centsToDollars(b.baseCents + b.dayOfWeekSurgeCents),
  };
}

/**
 * The frequency name that the store's `occurance` percentage stands for.
 *
 * The store keeps the discount percentage rather than the frequency, and 10%
 * maps to both "every 3 weeks" and "every 4 weeks" — so this direction is lossy
 * and cannot live in `@homero/pricing` next to the real table. It reproduces
 * exactly the labels the order summary has always shown; both now read it here
 * rather than each carrying their own ternary.
 */
export function frequencyLabelFor(occurance: number | string): string {
  switch (Number(occurance)) {
    case 20:
      return "Weekly";
    case 15:
      return "Every 2 weeks";
    case 10:
      return "Every 4 weeks";
    default:
      return "One Time";
  }
}

/**
 * Normalise whatever arrives in the `?t=` handoff from the landing page.
 *
 * Accepts a cleaning type id (`super_clean`), a name, or a price from any rate
 * card, and returns the id — the store's `types` format.
 *
 * The store used to hold the type's *price*, and that broke once an admin could
 * edit prices (17 Sep feedback): the options list captured the bundled card's
 * prices at module load, so a picked tile stored a price the live card no longer
 * had. The summary went blank, the description fell through to Move In / Out,
 * and `price <= 190` hid the plans from Special Mission once it cost $193. Two
 * types at one price ($288 on test) could not be told apart at all. The server
 * has accepted ids since the admin-config work.
 *
 * Note a bare price from an old card can be genuinely ambiguous — $248 was V2
 * Super Clean and later V3 Move In / Out — so links older than V4 may resolve
 * to the newer meaning. New links carry the id instead.
 */
export function normalizeTypesParam(value: string | null | undefined): CleaningTypeId {
  return resolveCleaningType(value, now()) ?? "standard";
}

/** What the customer calls the cleaning type, e.g. "Special Mission". */
export function cleaningTypeLabelFor(types: string): string {
  return CLEANING_TYPE_LABELS[resolveTypeId(types)];
}

/**
 * Whether this type can be booked as a recurring plan. Super Clean and
 * Move In / Out are one-off jobs — the same two that include the extras.
 */
export function offersPlans(types: string): boolean {
  return !isAddonFree(types);
}

/** Whether this cleaning type includes the oven/fridge/cabinet extras. */
export function isAddonFree(types: string): boolean {
  return includesFreeExtras(resolveTypeId(types), now());
}

/** The cost of the selected add-ons, in cents. */
export function addOnsCostCents(types: string, selected: string[]): number {
  return getAddOnsCostCents(resolveTypeId(types), selected, now());
}

/** The cost of the selected add-ons, in dollars, as the store holds it. */
export function addOnsCostDollars(types: string, selected: string[]): number {
  return centsToDollars(addOnsCostCents(types, selected));
}
